import type { IDemoSessionResponse } from '@/interfaces/lineDemo';

/** A visitor keeps a separate customer and conversation at each demo venue. */
export function createDemoSessionCache(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
) {
  const sessions = new Map<number, IDemoSessionResponse>();
  const pending = new Map<number, Promise<IDemoSessionResponse>>();
  const key = (locationId: number) => `demo_live_session_token:${locationId}`;
  const storedToken = (locationId: number) => storage.getItem(key(locationId));
  const set = (session: IDemoSessionResponse) => {
    sessions.set(session.location_id, session);
    storage.setItem(key(session.location_id), session.demo_live_session_token);
    return session;
  };

  return {
    get: (locationId: number) => sessions.get(locationId) ?? null,
    hasStarted: () => sessions.size > 0,
    storedToken,
    set,
    forget(locationId: number) {
      sessions.delete(locationId);
      storage.removeItem(key(locationId));
    },
    async request(
      locationId: number,
      forceRefresh: boolean,
      load: (token: string | undefined) => Promise<IDemoSessionResponse>
    ): Promise<IDemoSessionResponse> {
      const cached = sessions.get(locationId);
      if (
        !forceRefresh &&
        cached &&
        cached.access_token_expires_at * 1000 > Date.now()
      )
        return cached;
      const inFlight = pending.get(locationId);
      if (inFlight) return inFlight;

      const tokenSent =
        cached?.demo_live_session_token || storedToken(locationId) || undefined;
      const promise = load(tokenSent).then(response => {
        // Preserve a chat token rotated while this location's access JWT refreshed.
        const latest = sessions.get(locationId);
        return set(
          latest &&
            latest.agent_session_uuid === response.agent_session_uuid &&
            latest.demo_live_session_token !== tokenSent
            ? {
                ...response,
                demo_live_session_token: latest.demo_live_session_token,
              }
            : response
        );
      });
      pending.set(locationId, promise);
      try {
        return await promise;
      } finally {
        pending.delete(locationId);
      }
    },
  };
}
